import { TrendingUp, HardHat, Target, Check, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { Frame } from "@/components/site/Frame";
import { CTA } from "@/components/site/CTA";
import { PRINCIPLES } from "@/data/content";

const VALUES = [
  { icon: TrendingUp, title: "Growing", text: "Taking on larger and more complete projects, one step at a time." },
  { icon: HardHat, title: "Experienced", text: "Grounded in hands-on knowledge of how buildings are put together, from first fix to final finish." },
  { icon: Target, title: "Ambitious", text: "Aiming to deliver design, construction and development as one integrated service." },
];

const FROM = ["Refurbishment", "Extensions", "Loft conversions", "Conservatories", "Interior construction and finishing"];
const TO = ["Architectural engineering and design", "Complete new-build delivery", "Design & Build", "Development", "Larger residential and commercial opportunities"];

const Column = ({ title, items, accent, testId }) => (
  <div data-testid={testId} className={`p-8 sm:p-12 ${accent ? "bg-[#14171f]" : "bg-[#0c0e12]"}`}>
    <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-8">{title}</h3>
    <ul className="space-y-4">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-4 border-b border-[#262b38] pb-4">
          {accent ? <ArrowRight className="w-5 h-5 mt-0.5 text-[#d96b27] shrink-0" /> : <Check className="w-5 h-5 mt-0.5 text-[#a7aebb] shrink-0" />}
          <span className={accent ? "text-[#f4f5f7]" : "text-[#c9ced8]"}>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testId="about-hero"
        eyebrow="About Interior Box"
        lines={["A growing practice", "with bigger", "ambitions."]}
        intro="Interior Box has practical architectural and building capability today, and is expanding into complete design and build delivery. Based in Preston, we work across the North West."
        img="/img/extension.jpg"
      />

      <section className="mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-36 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4"><Eyebrow>Who we are</Eyebrow></Reveal>
        <Reveal delay={0.1} className="lg:col-span-8 space-y-8">
          <p className="font-display text-3xl sm:text-4xl font-medium leading-[1.2] tracking-tight">
            We carry out residential and commercial design, construction and finishing: refurbishment, extensions, loft conversions, conservatories, interior construction and finishing.
          </p>
          <p className="text-[#a7aebb] text-base md:text-lg leading-relaxed max-w-2xl">
            We are building on that practical base with architectural, technical and project capability, so that we can take on more complete projects and see them through from the first idea to the finished building.
          </p>
        </Reveal>
      </section>

      <section data-testid="about-values" className="mx-auto max-w-[1440px] px-5 sm:px-10 pb-28 sm:pb-36">
        <div className="grid md:grid-cols-3 gap-px bg-[#262b38] border border-[#262b38]">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1} className="bg-[#0c0e12] p-8 sm:p-10 min-h-[280px] flex flex-col justify-between group hover:bg-[#14171f] transition-colors">
              <div className="flex justify-between">
                <v.icon className="w-8 h-8 text-[#d96b27]" strokeWidth={1.4} />
                <span className="font-mono text-xs text-[#a7aebb]">0{i + 1}</span>
              </div>
              <div>
                <h3 className="font-display text-3xl font-bold tracking-tight">{v.title}</h3>
                <p className="text-[#a7aebb] mt-3 leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="about-direction" className="border-t border-[#262b38] mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-36">
        <Reveal>
          <Eyebrow>Trajectory</Eyebrow>
          <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight mt-6 mb-16 max-w-3xl">Where our work has grown from — and where it is heading.</h2>
        </Reveal>
        <Reveal className="grid md:grid-cols-2 gap-px bg-[#262b38] border border-[#262b38]">
          <Column title="Where our work has grown from" items={FROM} testId="about-from-list" />
          <Column title="Where we are heading" items={TO} accent testId="about-to-list" />
        </Reveal>
      </section>

      <section data-testid="about-principles" className="border-t border-[#262b38] mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-36">
        <div className="grid md:grid-cols-3 gap-8">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.1}>
              <Frame src={p.img} alt={p.title} className="aspect-[3/4]" testId={`about-principle-${p.n}`} />
              <span className="block font-mono text-xs text-[#d96b27] mt-6">{p.n}</span>
              <h3 className="font-display text-3xl font-bold tracking-tight mt-2">{p.title}</h3>
              <p className="text-[#a7aebb] mt-4 leading-relaxed">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-[#262b38] mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-36 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4"><Eyebrow>Leadership and team</Eyebrow></Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <p className="text-[#c9ced8] text-base md:text-lg leading-relaxed max-w-2xl">
            Profiles of the people who lead design and construction at Interior Box will be published here. Each profile will cover their role and background, and any professional memberships they hold.
          </p>
          <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-16">Direction and ambition</h3>
          <p className="text-[#a7aebb] text-base md:text-lg leading-relaxed max-w-2xl mt-6">
            Interior Box aims to grow into an integrated architecture, construction and development company, able to take on progressively more complex projects. That is a direction, not a claim about work already completed — we expect to earn each step by delivering the one before it well.
          </p>
        </Reveal>
      </section>

      <CTA />
    </div>
  );
}
